import React, { useState } from "react";
import {
  useDownloadArtifact,
  useDownloadEpicOcrPdf,
  useGetSDRArtifacts,
} from "../services/DocumentServices";

import { Button } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";

interface DatasetViewerDownloadButtonProps {
  row: {
    sdrID: number;
    [key: string]: any;
  };
  includeImageSources?: boolean;
}

const DatasetViewerDownloadButton: React.FC<
  DatasetViewerDownloadButtonProps
> = ({ row, includeImageSources }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: artifacts } = useGetSDRArtifacts(row.sdrID);
  const artifactId =
    artifacts && artifacts.length > 0 ? artifacts[0]?.id : null;

  const { refetch: downloadArtifact } = useDownloadArtifact(artifactId);
  const { refetch: downloadEpicOcrPdf } = useDownloadEpicOcrPdf(artifactId);

  const handleDownload = async () => {
    if (!artifactId) return;

    setIsDownloading(true);
    try {
      let data = null;

      // Prefer the Epic-OCR-processed PDF when image sources are indexed.
      if (includeImageSources) {
        try {
          const result = await downloadEpicOcrPdf();
          data = result.data;
        } catch {
          // Epic OCR PDF not available, fall through to original
        }
      }

      // Fall back to original artifact download
      if (!data) {
        const result = await downloadArtifact();
        data = result.data;
      }

      if (data) {
        // Create a blob URL from the downloaded data
        const url = URL.createObjectURL(data.blob);

        const mimeType = data.blob.type?.split(";")[0]?.trim();
        const derivedExtension = mimeType
          ? mimeType.includes("/")
            ? mimeType.split("/")[1]
            : ""
          : "";

        // Use filename from response header, fall back to row data or default
        const fileName =
          data.fileName ||
          row.data?.file_name ||
          `document-${row.sdrID}${derivedExtension ? `.${derivedExtension}` : ""}`;

        // Create a temporary anchor element to trigger the download
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        // Clean up
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      size="small"
      startIcon={<DownloadIcon />}
      onClick={handleDownload}
      disabled={isDownloading || !artifactId}
      data-attr="datasets:btn:download-document"
      sx={{ minWidth: "120px" }}
    >
      {isDownloading ? "Downloading..." : "Download"}
    </Button>
  );
};

export default DatasetViewerDownloadButton;
